import React, { useEffect, useMemo } from "react";

import { MetricsIntervalContext } from "lib/contexts";
import { MetricDataInterval } from "@tether-web-portal/types/metricsTypes";

/**
 * Reads the shared metrics-interval context (active time range + selected interval).
 *
 * @param initialDataInterval - Optional. When provided, the hook pushes this interval
 *   into the shared `MetricsIntervalContext` once on mount (overriding the current
 *   selection). When omitted, the hook sets nothing and simply inherits whatever
 *   interval the surrounding `MetricsIntervalContextProvider` already holds.
 *
 *   Default when omitted: the active interval is whatever the nearest provider was
 *   initialised with — its `defaultInterval` prop, which itself falls back to
 *   `MetricDataInterval.HOURS_24`. With no surrounding provider, the context's own
 *   default is also `MetricDataInterval.HOURS_24`. So unless a provider or user
 *   selection has changed it, the effective default is `MetricDataInterval.HOURS_24`.
 */
export const useMetricsInterval = (initialDataInterval?: MetricDataInterval) => {
  const context = React.useContext(MetricsIntervalContext);

  const initialDataIntervalToUse = useMemo(() => initialDataInterval, [initialDataInterval]);

  useEffect(() => {
    if (initialDataIntervalToUse && context.metricsCurrentDataInterval !== initialDataIntervalToUse) {
      context.setMetricsInterval(initialDataIntervalToUse);
    }

    context.setInitialized();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDataIntervalToUse]);

  return context;
};
